<template>
  <div class="common-email-enquiry" :class="{'--open': isOpen}">
    <div class="common-email-enquiry__inner">
      <!-- Header -->
      <div class="__header">
        <div class="__title">
          <h2>Email Enquiry</h2>
        </div>
        <div class="__close" @click="updateIsOpen(false)">
          <img v-lazy-load  :data-src="closeDarkIcon" alt="Close icon" width="20" height="20">
        </div>
      </div>

      <!-- Body -->
      <div class="__body row">
        <div class="col-12 form-fields">
          <ElementsInputsText v-model="name" v-model:is-valid="isValid" label="Name*" :rules="{fieldName: 'Name', rules: {required: true, minLength: 2, maxLength: 32}}" variant="underlined" />
        </div>
        <div class="col-12 form-fields">
          <ElementsInputsText v-model="email" v-model:is-valid="isValid" label="Email*" :rules="{fieldName: 'Email', rules: {required: true, email: true}}" variant="underlined" />
        </div>
        <div class="col-12 form-fields">
          <ElementsInputsText v-model="phone" v-model:is-valid="isValid" label="Phone" :rules="{fieldName: 'Phone number', rules: {phone: true}}" variant="underlined" />
        </div>
        <div class="col-12 form-fields">
          <ElementsInputsTextarea v-model="message" v-model:is-valid="isValid" label="Message*" rows="5" :rules="{fieldName: 'Message', rules: {required: true, maxLength: 500}}" variant="underlined" />
        </div>
      </div>

      <!-- Actions -->
      <div class="__actions">
        <div class="__response" :class="response !== 200 ? '--error' : '--success'">
          <span v-if="response">{{ response === 200 ? 'Your enquiry has been sent successfully.' : 'Error sending your enquiry. Please try again later.' }}</span>
        </div>
        <ElementsButtonsRegular label="Send" :loading="isLoading" @click="sendEnquiry" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import closeDarkIcon from '~/assets/images/icons/close-dark.png';
import {Ref} from 'vue';
// EMIT Definitions
//----------------------------------------------------------------------------------------------------------------------
const emit = defineEmits<{
  (event: 'update:isOpen', payload: boolean): void;
}>();

// PROPS
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  enquiryType: {
    type: String as PropType<'listing' | 'builder' | 'development'>,
    required: true,
  },
  enquiringEntityId: {
    type: String,
    required: true,
  },
  trackingEventName: {
    type: String,
    required: false,
    default: '',
  },
});

// DATA
const name: Ref<string> = ref('');
const email: Ref<string> = ref('');
const phone: Ref<string> = ref('');
const message: Ref<string> = ref('');
const isLoading: Ref<boolean> = ref(false);
const response: Ref<number | null> = ref(null);
const isValid: Ref<string[]> = ref([]);
const {$emailEnquiry, $metaPixelTrackEvent} = useNuxtApp();

/**
 * Update user data
 */
async function sendEnquiry () {
  // Return of the form is loading
  if (isLoading.value) {
    return;
  }

  // Run validating by changing the value (fields have a watch on them and those will run the validation when isValid is changed)
  isValid.value = ['validate'];
  nextTick(async () => {
    if (!(isValid.value.filter((item) => item !== 'validate').length)) {
      isLoading.value = true;

      response.value = await $emailEnquiry({
        name: name.value,
        email: email.value,
        phone: phone.value,
        message: message.value,
        entity: props.enquiryType,
        entityId: props.enquiringEntityId,
      });


      if (response.value === 200) {
        name.value = '';
        email.value = '';
        phone.value = '';
        message.value = '';

        setTimeout(() => {
          updateIsOpen(false);
        }, 3000);
      }

      setTimeout(() => {
        isLoading.value = false;
      }, 500);

      // Trigger pixel lead event
      triggerPixelLeadEvent();
    }
  });
}

/**
 * Trigger pixel lead event
 */
function triggerPixelLeadEvent () {
  // Track meta event
  $metaPixelTrackEvent('Lead', {
    content_name: props.trackingEventName,
    content_category: props.enquiryType,
    content_ids: [props.enquiringEntityId],
    content_type: props.enquiryType,
    lead_type: 'email',
  });
}


/**
 * Update model value
 */
function updateIsOpen (isOpen: boolean) {
  emit('update:isOpen', isOpen);
}
</script>

<style lang="scss" scoped>
.common-email-enquiry {
  display: flex;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  margin: auto;
  width: 100%;
  max-width: 640px;
  background: #fff;
  height: 0;
  overflow: hidden;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
  z-index: 9999;
  align-items: flex-start;
  transition: height 0.2s ease-in-out;

  &.--open {
    height: 540px;
    box-shadow: 0 -2px 10px 4px rgba(0, 0, 0, 0.4);
  }

  .common-email-enquiry__inner {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 20px;

    .__header {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;

      .__title h2{
        font-size: 22px;
      }

      .__close {
        cursor: pointer;
        scale: 1;
        transition: all 0.2s ease-in-out;

        &:hover {
          scale: 1.2;
        }
      }
    }

    .__body {
      .form-fields {
        margin-bottom: 20px;
      }

      .el-input-text,
       .el-input-textarea {
         margin-top: 12px;
       }
    }

    .__actions {
      display: flex;
      justify-content: space-between;
      width: 100%;

      .__response {
        &.--error {
          color: #d30101;
        }
        &.--success {
          color: #00b300;
        }
        font-size: 18px;
        margin-top: 12px;
        padding-left: 12px;
      }
    }
  }
}
</style>
